import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    ArrowRight,
    Star,
    BadgeCheck,
    ExternalLink,
} from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import {
    PAGE_META,
    AGGREGATE_RATING,
    REVIEW_SOURCES,
} from "@/lib/seo-constants";
import {
    breadcrumbListSchema,
    reviewsItemListSchema,
    webPageSchema,
} from "@/lib/jsonld";
import { TESTIMONIALS } from "@/data/testimonials";
import { REVIEW_SOURCE_CONTENT } from "@/data/review-source-content";
import { cn } from "@/lib/utils";

const SOURCE_ORDER = ["Homestars", "Bark", "Houzz", "BBB", "Google"];

const META_BY_SLUG = {
    google: PAGE_META.reviewsGoogle,
    homestars: PAGE_META.reviewsHomestars,
    bark: PAGE_META.reviewsBark,
    houzz: PAGE_META.reviewsHouzz,
    bbb: PAGE_META.reviewsBbb,
};

const formatDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("en-CA", {
            year: "numeric",
            month: "short",
        });
    } catch {
        return null;
    }
};

const platformMeta = (name) => {
    const meta = REVIEW_SOURCES[name];
    if (!meta) return "";
    if (meta.accreditation) {
        return `Accredited · ${meta.accreditation} · ${meta.count} reviews`;
    }
    const noun = meta.count === 1 ? "review" : "reviews";
    return `${meta.count} ${noun} · ${meta.rating.toFixed(1)}★`;
};

const StarRow = () => (
    <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
            <Star
                key={i}
                className="h-4 w-4 text-brand-cyan fill-brand-cyan"
                strokeWidth={1.5}
            />
        ))}
    </div>
);

const PlatformPill = ({ name }) => {
    const meta = REVIEW_SOURCES[name];
    if (!meta) return null;
    return (
        <a
            href={meta.profileUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="group flex flex-col items-start gap-1 bg-white border border-brand-dark/10 px-4 py-3 hover:border-brand-cyan transition-colors min-w-[150px]"
            data-testid={`reviews-platform-pill-${name.toLowerCase()}`}
        >
            <span className="text-sm font-semibold uppercase tracking-wider text-brand-dark group-hover:text-brand-cyan transition-colors">
                {name}
            </span>
            <span className="text-xs text-brand-gray">{platformMeta(name)}</span>
        </a>
    );
};

const ReviewCard = ({ t }) => {
    const source = REVIEW_SOURCES[t.source] || {};
    const dateLabel = formatDate(t.date);
    return (
        <article
            className="bg-white border border-brand-dark/10 p-6 md:p-8 flex flex-col h-full hover:border-brand-cyan transition-colors"
            data-testid={`review-card-${t.id}`}
        >
            <div className="flex items-center justify-between mb-4">
                <StarRow />
                {t.verified && (
                    <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-brand-cyan bg-brand-cyan/10 px-2 py-1"
                        data-testid={`review-card-${t.id}-verified`}
                    >
                        <BadgeCheck className="h-3 w-3" strokeWidth={2.5} />
                        Verified
                    </span>
                )}
            </div>
            <blockquote className="font-light italic text-base text-brand-dark/85 leading-relaxed flex-1">
                {`"${t.fullQuote || t.quote}"`}
            </blockquote>
            <footer className="mt-5 pt-5 border-t border-brand-dark/10 flex items-end justify-between gap-3">
                <div>
                    <p className="font-semibold text-sm text-brand-dark">{t.name}</p>
                    <p className="text-xs text-brand-gray">
                        via {t.source}
                        {dateLabel && <span> · {dateLabel}</span>}
                    </p>
                </div>
                <a
                    href={source.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-brand-coral hover:text-brand-cyan transition-colors whitespace-nowrap"
                    data-testid={`review-card-${t.id}-source-link`}
                >
                    View on {t.source}
                    <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
                </a>
            </footer>
        </article>
    );
};

/**
 * Reviews page.  When `sourceSlug` is set, the page becomes an indexable
 * landing page for that specific platform: locked filter, source-specific
 * hero, intro paragraph, SEO meta, and breadcrumb.
 */
export default function Reviews({ sourceSlug = null }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryFilter = (searchParams.get("source") || "all").toLowerCase();
    const activeFilter = sourceSlug ? sourceSlug.toLowerCase() : queryFilter;
    const locked = Boolean(sourceSlug);

    const sourceContent = sourceSlug ? REVIEW_SOURCE_CONTENT[sourceSlug] : null;
    const path = sourceSlug ? `/reviews/${sourceSlug}` : "/reviews";

    const filtered = useMemo(() => {
        if (activeFilter === "all") return TESTIMONIALS;
        return TESTIMONIALS.filter(
            (t) => t.source.toLowerCase() === activeFilter,
        );
    }, [activeFilter]);

    const handleFilter = (key) => {
        if (locked) return;
        const next = new URLSearchParams(searchParams);
        if (key === "all") next.delete("source");
        else next.set("source", key);
        setSearchParams(next, { replace: true });
    };

    const FILTERS = [
        { key: "all", label: "All Sources", count: TESTIMONIALS.length },
        ...SOURCE_ORDER.map((name) => ({
            key: name.toLowerCase(),
            label: name,
            count: TESTIMONIALS.filter((t) => t.source === name).length,
        })),
    ];

    // SEO meta — per-source overrides default reviews meta.
    const seoMeta = sourceSlug ? META_BY_SLUG[sourceSlug] : PAGE_META.reviews;
    const breadcrumbItems = sourceSlug
        ? [
              { name: "Reviews", path: "/reviews" },
              { name: sourceContent?.sourceName || sourceSlug, path },
          ]
        : [{ name: "Reviews", path: "/reviews" }];

    const jsonLd = [
        breadcrumbListSchema(breadcrumbItems),
        reviewsItemListSchema(filtered),
    ];
    if (sourceSlug) {
        jsonLd.push(
            webPageSchema({
                name: seoMeta.title,
                description: seoMeta.description,
                path,
            }),
        );
    }

    // Hero copy & summary copy adapt for the source-specific case.
    const heroTitle = sourceContent
        ? `${sourceContent.sourceName} Reviews.`
        : "What homeowners say.";
    const heroTagline = sourceContent
        ? `Verified ${sourceContent.sourceName} reviews of Reformity Inc. — straight from the platform.`
        : "Verified reviews from Homestars, Bark, Houzz, BBB, and Google. No edits. No curation.";

    return (
        <>
            <Seo {...seoMeta} path={path} jsonLd={jsonLd} />

            {/* Hero strip */}
            <Section dark className="py-14 md:py-16" data-testid="reviews-hero">
                <Container>
                    <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4">
                        {sourceContent ? sourceContent.kicker : "Verified Customer Feedback"}
                    </p>
                    <h1
                        className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-none text-white"
                        data-testid="reviews-title"
                    >
                        {heroTitle}
                    </h1>
                    <p className="mt-5 max-w-2xl text-base md:text-lg text-white/75 font-light italic leading-relaxed">
                        {heroTagline}
                    </p>
                </Container>
            </Section>

            {/* Per-source intro block (only on /reviews/<slug>) */}
            {sourceContent && (
                <Section
                    className="py-12 md:py-14"
                    bg="white"
                    data-testid={`reviews-source-intro-${sourceSlug}`}
                >
                    <Container>
                        <div className="max-w-3xl">
                            <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                                {sourceContent.introHeading}
                            </p>
                            <p className="text-base md:text-lg text-brand-dark/80 leading-relaxed">
                                {sourceContent.intro}
                            </p>
                            <p className="mt-5 text-sm text-brand-dark/65">
                                See the full picture on{" "}
                                <Link
                                    to="/reviews"
                                    className="text-brand-cyan hover:underline font-semibold"
                                    data-testid="reviews-source-intro-all-link"
                                >
                                    all 157 verified reviews →
                                </Link>
                            </p>
                        </div>
                    </Container>
                </Section>
            )}

            {/* Summary card */}
            <Section className="py-14 md:py-16" data-testid="reviews-summary">
                <Container>
                    <div
                        className="bg-white border border-brand-dark/10 p-8 md:p-10"
                        data-testid="reviews-summary-card"
                    >
                        <div className="flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-10">
                            <div className="lg:flex-1">
                                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                                    Aggregate Rating
                                </p>
                                <p
                                    className="font-display text-6xl sm:text-7xl tracking-wide text-brand-dark leading-none"
                                    data-testid="reviews-summary-headline"
                                >
                                    {AGGREGATE_RATING.average.toFixed(1)}
                                    <span className="text-brand-cyan">★</span>
                                </p>
                                <p className="mt-4 text-base text-brand-dark/75 max-w-md">
                                    Weighted average across{" "}
                                    <span className="font-semibold text-brand-dark">
                                        {AGGREGATE_RATING.total} verified reviews
                                    </span>{" "}
                                    on 5 platforms.
                                </p>
                            </div>
                            <div className="lg:flex-1 flex flex-wrap gap-3">
                                {SOURCE_ORDER.map((name) => (
                                    <PlatformPill key={name} name={name} />
                                ))}
                            </div>
                        </div>
                    </div>
                </Container>
            </Section>

            {/* Filter chips */}
            <Section
                className="py-6 md:py-8"
                bg="white"
                data-testid="reviews-filters"
            >
                <Container>
                    <div
                        className="flex flex-wrap items-center gap-2"
                        role="tablist"
                        aria-label="Filter reviews by source"
                    >
                        {FILTERS.map((f) => {
                            const active = activeFilter === f.key;
                            const disabled = locked && !active;
                            return (
                                <button
                                    key={f.key}
                                    type="button"
                                    onClick={() => handleFilter(f.key)}
                                    role="tab"
                                    aria-selected={active}
                                    disabled={disabled}
                                    className={cn(
                                        "inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm uppercase tracking-wider font-semibold transition-colors duration-200 border",
                                        active
                                            ? "bg-brand-dark text-white border-brand-dark"
                                            : disabled
                                              ? "bg-white text-brand-dark/35 border-brand-dark/10 cursor-not-allowed"
                                              : "bg-white text-brand-dark border-brand-dark/15 hover:border-brand-cyan hover:text-brand-cyan",
                                    )}
                                    data-testid={`reviews-filter-${f.key}`}
                                >
                                    {f.label}
                                    <span
                                        className={cn(
                                            "text-[10px]",
                                            active ? "text-white/70" : "text-brand-gray",
                                        )}
                                    >
                                        ({f.count})
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </Container>
            </Section>

            {/* Reviews grid */}
            <Section
                className="py-10 md:py-16"
                bg="white"
                data-testid="reviews-grid-section"
            >
                <Container>
                    {filtered.length === 0 ? (
                        <p
                            className="text-center text-brand-dark/70 py-12"
                            data-testid="reviews-empty"
                        >
                            No reviews for this source yet.
                        </p>
                    ) : (
                        <div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
                            data-testid="reviews-grid"
                        >
                            {filtered.map((t) => (
                                <ReviewCard key={t.id} t={t} />
                            ))}
                        </div>
                    )}
                </Container>
            </Section>

            {/* More verified reviews rail — only on /reviews/<source> pages.
                Cross-links each source page to the OTHER 4 platform pages so
                Google sees a strong internal link graph between them. */}
            {sourceSlug && (
                <Section
                    className="py-12 md:py-16"
                    bg="white"
                    data-testid="reviews-more-rail"
                >
                    <Container>
                        <h3
                            className="font-display text-3xl sm:text-4xl tracking-wide text-brand-dark leading-none mb-8"
                            data-testid="reviews-more-rail-heading"
                        >
                            More verified reviews.
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                            {SOURCE_ORDER.filter(
                                (n) => n.toLowerCase() !== sourceSlug,
                            ).map((name) => {
                                const meta = REVIEW_SOURCES[name];
                                if (!meta) return null;
                                const slug = name.toLowerCase();
                                return (
                                    <Link
                                        key={name}
                                        to={`/reviews/${slug}`}
                                        className="group flex flex-col gap-1 bg-white border border-brand-dark/10 px-4 py-4 hover:border-brand-cyan hover:-translate-y-0.5 transition-all duration-200"
                                        data-testid={`reviews-more-rail-${slug}`}
                                    >
                                        <span className="text-sm font-semibold uppercase tracking-wider text-brand-dark group-hover:text-brand-cyan transition-colors">
                                            {name}
                                        </span>
                                        <span className="text-xs text-brand-gray">
                                            {platformMeta(name)}
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>
                    </Container>
                </Section>
            )}

            {/* CTA band */}
            <Section dark className="py-16 md:py-20" data-testid="reviews-cta-band">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                                Like what you see?
                            </p>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide text-white leading-none">
                                Get a Free Quote.
                            </h2>
                            <p className="mt-5 text-base text-white/70 max-w-xl">
                                Join {AGGREGATE_RATING.total}+ homeowners across the GTA
                                who&apos;ve trusted Reformity with their fence, deck,
                                driveway, or reno.
                            </p>
                        </div>
                        <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                            <BrandButton
                                asChild
                                variant="primary"
                                size="lg"
                                className="w-full lg:w-auto"
                            >
                                <Link to="/contact" data-testid="reviews-cta-quote">
                                    Start My Free Quote
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </BrandButton>
                        </div>
                    </div>
                </Container>
            </Section>
        </>
    );
}
